import React, { useMemo, useState } from 'react';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import './ResumeReview.css';

type Scorecard = {
  leadership: number;
  impact: number;
  clarity: number;
  deferredFit: number;
};

type ResumeFeedback = {
  scorecard: Scorecard;
  strengths: string[];
  improvements: string[];
  rewrittenBullets: string[];
  summary: string;
};

const EMPTY_FEEDBACK: ResumeFeedback = {
  scorecard: {
    leadership: 0,
    impact: 0,
    clarity: 0,
    deferredFit: 0
  },
  strengths: [],
  improvements: [],
  rewrittenBullets: [],
  summary: ''
};

function sanitizeScore(v: unknown): number {
  if (typeof v !== 'number' || Number.isNaN(v)) return 0;
  return Math.min(10, Math.max(0, Math.round(v)));
}

function normalizeStringArray(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .slice(0, limit);
}

function parseFeedback(raw: string): ResumeFeedback {
  const trimmed = raw.trim();
  const jsonText = (() => {
    const first = trimmed.indexOf('{');
    const last = trimmed.lastIndexOf('}');
    if (first === -1 || last === -1 || last <= first) return trimmed;
    return trimmed.slice(first, last + 1);
  })();
  const parsed = JSON.parse(jsonText) as {
    scorecard?: Partial<Scorecard>;
    strengths?: unknown;
    improvements?: unknown;
    rewrittenBullets?: unknown;
    summary?: unknown;
  };

  return {
    scorecard: {
      leadership: sanitizeScore(parsed.scorecard?.leadership),
      impact: sanitizeScore(parsed.scorecard?.impact),
      clarity: sanitizeScore(parsed.scorecard?.clarity),
      deferredFit: sanitizeScore(parsed.scorecard?.deferredFit)
    },
    strengths: normalizeStringArray(parsed.strengths, 5),
    improvements: normalizeStringArray(parsed.improvements, 6),
    rewrittenBullets: normalizeStringArray(parsed.rewrittenBullets, 5),
    summary: typeof parsed.summary === 'string' ? parsed.summary.trim() : ''
  };
}

const ResumeReview: React.FC = () => {
  const [resumeText, setResumeText] = useState('');
  const [feedback, setFeedback] = useState<ResumeFeedback>(EMPTY_FEEDBACK);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);

  const canAnalyze = !isGenerating && resumeText.trim().length > 0;
  const hasFeedback =
    feedback.summary.length > 0 ||
    feedback.strengths.length > 0 ||
    feedback.improvements.length > 0 ||
    feedback.rewrittenBullets.length > 0;

  const wordCount = useMemo(() => {
    const trimmed = resumeText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [resumeText]);

  const handleAnalyzeResume = async () => {
    const trimmed = resumeText.trim();
    if (!trimmed) return;
    setIsGenerating(true);
    setMessage(null);
    setMessageType(null);

    try {
      const completion = await fetchOpenAIChatCompletion({
        model: 'gpt-4o-mini',
        temperature: 0.2,
        max_tokens: 1200,
        messages: [
          {
            role: 'system',
            content: `You are an elite deferred MBA resume reviewer.
Return STRICT JSON only with keys:
{
  "scorecard": { "leadership": number, "impact": number, "clarity": number, "deferredFit": number },
  "strengths": string[],
  "improvements": string[],
  "rewrittenBullets": string[],
  "summary": string
}
Rules:
- Scores are integers 0-10.
- Tailor to deferred MBA applicants (internships, campus leadership, early-career narrative).
- Keep strengths to max 5, improvements max 6, rewrittenBullets max 5.
- Rewritten bullets must preserve facts; do not invent metrics.
- Summary should be 2-3 concise sentences.`
          },
          {
            role: 'user',
            content: `Review this resume text:\n\n${trimmed}`
          }
        ]
      });

      const parsed = parseFeedback(completion);
      setFeedback(parsed);
      setMessage('Resume feedback generated.');
      setMessageType('success');
    } catch (err) {
      console.error(err);
      setMessage('Could not analyze resume right now. Please try again.');
      setMessageType('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const scoreRows = [
    { key: 'leadership', label: 'Leadership' },
    { key: 'impact', label: 'Impact' },
    { key: 'clarity', label: 'Clarity' },
    { key: 'deferredFit', label: 'Deferred-MBA Fit' }
  ] as const;

  return (
    <div className="resume-review-page">
      <div className="resume-review-container">
        <header className="resume-review-header">
          <h1 className="resume-review-title">Resume Review</h1>
          <p className="resume-review-subtitle">
            Paste your resume text for deferred-MBA-specific feedback, scorecard ratings, and stronger bullet suggestions.
          </p>
        </header>

        <section className="resume-review-grid">
          <article className="resume-review-card">
            <div className="resume-review-card-head">
              <h2>Resume Input</h2>
              <span>{wordCount} words</span>
            </div>
            <textarea
              className="resume-review-input"
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your resume text here (education, internships, bullets, projects, leadership, awards)..."
            />
            <div className="resume-review-actions">
              <button
                type="button"
                className="resume-review-generate"
                disabled={!canAnalyze}
                onClick={() => void handleAnalyzeResume()}
              >
                {isGenerating ? 'Analyzing Resume...' : 'Analyze Resume'}
              </button>
            </div>
          </article>

          <article className="resume-review-card">
            <div className="resume-review-card-head">
              <h2>Deferred MBA Feedback</h2>
              <span>{hasFeedback ? 'Ready' : 'Empty'}</span>
            </div>

            {hasFeedback ? (
              <div className="resume-review-feedback">
                <div className="resume-review-scorecard">
                  {scoreRows.map((row) => (
                    <div key={row.key} className="resume-review-score-row">
                      <span>{row.label}</span>
                      <strong>{feedback.scorecard[row.key]}/10</strong>
                    </div>
                  ))}
                </div>

                <section className="resume-review-section">
                  <h3>Strengths</h3>
                  <ul>
                    {feedback.strengths.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="resume-review-section">
                  <h3>Improvements</h3>
                  <ul>
                    {feedback.improvements.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="resume-review-section">
                  <h3>Bullet Rewrites</h3>
                  <ul>
                    {feedback.rewrittenBullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                {feedback.summary && (
                  <section className="resume-review-section">
                    <h3>Summary</h3>
                    <p>{feedback.summary}</p>
                  </section>
                )}
              </div>
            ) : (
              <div className="resume-review-empty">
                Your deferred MBA resume feedback will appear here with scorecards, strengths, improvement priorities, and rewritten bullet suggestions.
              </div>
            )}
          </article>
        </section>

        {message && (
          <div className={`resume-review-message ${messageType === 'error' ? 'error' : 'success'}`}>{message}</div>
        )}

        <p className="resume-review-footnote">
          Cost note: this is one text-only AI analysis call and no Supabase writes by default, so it remains lightweight.
        </p>
      </div>
    </div>
  );
};

export default ResumeReview;

